import * as React from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  groupHash,
  groupLabel,
  VinalfaGroup,
} from "../../../interfaces/Vinalfa";
import theme from "../../../theme";

type Props = {
  groups: VinalfaGroup[];
  selectedHash: string | null;
  expandedHashes: Set<string>;
  childrenByHash: Record<string, VinalfaGroup[]>;
  loadingHash: string | null;
  onSelect: (group: VinalfaGroup) => void;
  onToggleExpand: (group: VinalfaGroup) => void;
};

const VinalfaCatalogSidebar = ({
  groups,
  selectedHash,
  expandedHashes,
  childrenByHash,
  loadingHash,
  onSelect,
  onToggleExpand,
}: Props) => {
  const renderNode = (group: VinalfaGroup, depth = 0) => {
    const hash = groupHash(group);
    const label = groupLabel(group);
    const isSelected = selectedHash === hash;
    const isExpanded = expandedHashes.has(hash);
    const kids = childrenByHash[hash];
    const hasKidsHint =
      group.hasChildren !== false &&
      (group.hasChildren === true ||
        (Array.isArray(group.children) && group.children.length > 0) ||
        kids !== undefined ||
        depth === 0);

    return (
      <React.Fragment key={hash || label}>
        <ListItemButton
          selected={isSelected}
          onClick={() => onSelect(group)}
          sx={{
            pl: 1.5 + depth * 1.5,
            borderLeft: isSelected
              ? `3px solid ${theme.palette.secondary.main}`
              : "3px solid transparent",
            "&.Mui-selected": {
              bgcolor: "rgba(0,0,0,0.06)",
            },
          }}
        >
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontFamily: "Inter",
              fontWeight: isSelected ? 600 : 500,
              fontSize: 14,
            }}
          />
          {hasKidsHint && (
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(group);
              }}
              sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
            >
              {loadingHash === hash ? (
                <CircularProgress size={14} />
              ) : isExpanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </Box>
          )}
        </ListItemButton>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {(kids || group.children || []).map((child) =>
              renderNode(child, depth + 1)
            )}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  if (!groups.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No catalog groups for this vehicle.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid #eee",
        borderRadius: 1,
        bgcolor: "#fff",
        maxHeight: 560,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: theme.palette.secondary.main,
          color: "#fff",
        }}
      >
        <Typography sx={{ fontFamily: "Inter", fontWeight: 600, fontSize: 13 }}>
          Catalog
        </Typography>
      </Box>
      <List dense disablePadding>
        {groups.map((g) => renderNode(g))}
      </List>
    </Box>
  );
};

export default VinalfaCatalogSidebar;
